<template>
  <v-card-round
    :color="color"
    :elevation="elevation"
    :hover="hover"
    :ripple="ripple"
    @click="$emit('click', product)"
  >
    <!-- The QProductHistoryId of the item -->
    <v-layout justify-center>
      <v-flex class="caption grey--text font-weight-light" shrink>{{
        product.id
      }}</v-flex>
    </v-layout>
    <!-- The image of the product -->
    <slot name="image" :product="product">
      <v-card-product-quantity :quantity="product.quantity" />
      <v-card-product-image :src="product.image" />
    </slot>
    <!-- Content of the card -->
    <slot name="content" :product="product">
      <v-container fluid class="pa-3">
        <!-- The name of the seller only if it's present -->
        <v-layout v-if="product.sellerName" justify-center>
          <v-flex class="grey--text my-2" shrink
            >{{ product.sellerName }} ({{ product.sellerId }})</v-flex
          >
        </v-layout>
        <!-- Brand & probability -->
        <slot name="before-title" :product="product">
          <v-layout align-center>
            <v-card-product-brand v-if="product.brand" :brand="product.brand" />
            <v-card-product-probability
              v-if="product.similarity"
              :probability="product.similarity"
            />
          </v-layout>
        </slot>
        <!-- Name && URL -->
        <slot name="title" :product="product">
          <v-layout align-center>
            <v-card-product-name :name="product.name" />
            <v-card-product-url v-if="product.url" :url="product.url" />
          </v-layout>
        </slot>
        <!-- Default slot for more content -->
        <slot />
      </v-container>
    </slot>
  </v-card-round>
</template>

<script>
import VCardProductBrand from "./CardProductBrand";
import VCardProductQuantity from "./CardProductQuantity";
import VCardProductImage from "./CardProductImage";
import VCardProductName from "./CardProductName";
import VCardProductProbability from "./CardProductProbability";
import VCardProductUrl from "./CardProductUrl";
import VCardRound from "./CardRound";

/**
 * A component that renders a card with the given product.
 *
 * It emits the 'click' event with the product object.
 *
 * It also has slots to customize the content (all the slots have the product as property):
 * - image: to show the image of the product.
 * - content: to show the information of the product with slots:
 *   - before-title: that by default shows the brand and probability of the product.
 *   - title: that by default shows the product's name and a button to open its url.
 *   - default: to add more content to the card.
 */
export default {
  components: {
    VCardProductBrand,
    VCardProductImage,
    VCardProductName,
    VCardProductProbability,
    VCardProductUrl,
    VCardProductQuantity,
    VCardRound,
  },
  props: {
    /**
     * The product to show in this card.
     * This component will use the name, brand of the product and optionally (only
     * if they are present) the image, probability and url of the product.
     */
    product: {
      type: Object,
      required: true,
    },
    /**
     * The color of the card.
     */
    color: {
      type: String,
    },
    /**
     * The elevation of the card.
     */
    elevation: {
      type: Number,
      default: 0,
    },
    /**
     * Elevate the card on hover.
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * Apply the ripple directive on the card.
     */
    ripple: {
      type: Boolean,
      default: false,
    },
  },
};
</script>